import React, { useState, useEffect, useCallback } from "react";
import { Handshake, Shield, ArrowLeft, Crown, KeyRound, Save, Power, Mail, Wand2, Eye, EyeOff, Sparkles } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { merge } from "../hooks/useIsMobile";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { passwordError, PASSWORD_HINT, generatePassword } from "../lib/passwordPolicy";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
  ok: "#166534",
  okBg: "#f0fdf4",
  okBorder: "#bbf7d0",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function AdminUserEdit() {
  const navigate = useNavigate();
  const { userId } = useParams();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth(`/api/ai/admin/users/${userId}`);

  const [user, setUser] = useState(null);
  const [betaEntries, setBetaEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // profile form
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEnabled, setUserEnabled] = useState(false);
  const [betaUser, setBetaUser] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [profileErr, setProfileErr] = useState(null);

  // password form
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwErr, setPwErr] = useState(null);

  const generatePw = () => {
    const pw = generatePassword();
    setPw1(pw);
    setPw2(pw);
    setShowPw(true);
    setPwErr(null);
    setPwMsg(null);
  };

  const load = useCallback(async (header) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users/${userId}`, {
        headers: { Authorization: header },
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setUser(data.user);
      setBetaEntries(data.beta_entries || []);
      setUsername(data.user.username || "");
      setEmail(data.user.email || "");
      setIsAdmin(!!data.user.is_admin);
      setUserEnabled(!!data.user.user_enabled);
      setBetaUser(!!data.user.beta_user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, handleUnauthorized]);

  useEffect(() => {
    if (authHeader) load(authHeader);
  }, [authHeader, load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileErr(null);
    setSavingProfile(true);
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users/${userId}`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, is_admin: isAdmin, user_enabled: userEnabled, beta_user: betaUser }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setUser((u) => ({ ...u, username, email, is_admin: isAdmin, user_enabled: userEnabled, beta_user: betaUser }));
      setProfileMsg("Profile saved.");
    } catch (err) {
      setProfileErr(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (sendEmail) => {
    setPwMsg(null);
    setPwErr(null);
    const pwError = passwordError(pw1);
    if (pwError) {
      setPwErr(pwError);
      return;
    }
    if (pw1 !== pw2) {
      setPwErr("The two passwords do not match.");
      return;
    }
    setSavingPw(sendEmail ? "email" : "password");
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users/${userId}/password`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw1, send_credentials_email: sendEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      // Deliberately NOT clearing pw1/pw2 here: "Update password" and "Send
      // email with credentials" are separate buttons over the same fields —
      // clearing after one would break clicking the other right after with
      // the same password (it'd validate against an empty string instead).
      if (data.emailed) {
        setPwMsg("Password updated and emailed to the user.");
      } else if (sendEmail && data.emailError) {
        setPwMsg(`Password updated, but the email failed to send: ${data.emailError}`);
      } else {
        setPwMsg("Password updated.");
      }
    } catch (err) {
      setPwErr(err.message);
    } finally {
      setSavingPw(false);
    }
  };

  // Emails a login link WITHOUT changing the user's existing password — used
  // when the password fields are left blank (nothing new to set/email).
  const sendLoginLink = async () => {
    setPwMsg(null);
    setPwErr(null);
    setSavingPw("email");
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/users/${userId}/send-login-link`, {
        method: "POST",
        headers: { Authorization: authHeader },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.emailed) {
        setPwMsg("Login link emailed to the user.");
      } else if (data.emailError) {
        setPwMsg(`Login link created, but the email failed to send: ${data.emailError}`);
      } else {
        setPwMsg("Login link created.");
      }
    } catch (err) {
      setPwErr(err.message);
    } finally {
      setSavingPw(false);
    }
  };

  const onSendEmailClick = () => (pw1 || pw2 ? savePassword(true) : sendLoginLink());

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input
              type="text"
              autoComplete="username"
              placeholder="Admin username"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              style={styles.loginInput}
              autoFocus
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin password"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              style={styles.loginInput}
            />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button
              type="button"
              onClick={() => navigate("/admin/users")}
              style={styles.loginGhost}
            >Back to users</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader onLogout={handleAdminLogout} showNewDispute={false} />

      <main style={styles.main}>
        <button onClick={() => navigate("/admin/users")} style={styles.backLink}>
          <ArrowLeft size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Back to users
        </button>

        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>
              Edit user
              {user?.beta_user && <span style={styles.betaBadge}>Beta user</span>}
            </h1>
            <p style={styles.subtitle}>
              {loading ? "Loading…" : user ? <>#{user.id} · joined {new Date(user.created_at).toLocaleDateString()}</> : "—"}
            </p>
          </div>
        </div>

        {error && <div style={styles.errorBox}>Failed to load: {error}</div>}

        {user && (
          <div style={styles.grid}>
            {/* Profile card */}
            <form style={styles.card} onSubmit={saveProfile}>
              <h2 style={styles.cardTitle}>Profile</h2>
              {user.is_google_user && (
                <div style={styles.noteBox}>
                  This is a Google account. Editing the email here does not change their Google login.
                </div>
              )}
              {betaEntries.length > 0 && (
                <div style={styles.noteBox}>
                  <strong>Beta signup / dispute feedback</strong>
                  {betaEntries.map((e, i) => (
                    <div key={e.id} style={i > 0 ? styles.betaEntry : { marginTop: 6 }}>
                      <div style={styles.betaEntryDate}>{new Date(e.created_at).toLocaleString()}</div>
                      {e.use_case && <div><strong>Dispute type:</strong> {e.use_case}</div>}
                      {e.context && <div><strong>Signup notes:</strong> {e.context}</div>}
                      {e.process_feedback && <div><strong>Process feedback:</strong> {e.process_feedback}</div>}
                      {e.outcome_feedback && <div><strong>Outcome feedback:</strong> {e.outcome_feedback}</div>}
                    </div>
                  ))}
                </div>
              )}
              <label style={styles.label}>Username</label>
              <input style={styles.input} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />

              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />

              <label style={styles.checkRow}>
                <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
                <Crown size={14} strokeWidth={2} />
                <span>Administrator</span>
              </label>

              <label style={styles.switchRow} title="Whether this account is allowed to log in">
                <span style={merge(styles.switchTrack, styles.switchTrackOn, userEnabled)}>
                  <span style={merge(styles.switchThumb, styles.switchThumbOn, userEnabled)} />
                </span>
                <input
                  type="checkbox"
                  checked={userEnabled}
                  onChange={(e) => setUserEnabled(e.target.checked)}
                  style={styles.switchInput}
                />
                <Power size={14} strokeWidth={2} />
                <span>{userEnabled ? "Account enabled" : "Account disabled (cannot log in)"}</span>
              </label>
              {userEnabled !== !!user.user_enabled && (
                <div style={styles.unsavedNote}>Unsaved — click "Save profile" to apply.</div>
              )}

              <label style={styles.switchRow} title="Whether this account counts as a beta user (bypasses the public-access gate when it's closed)">
                <span style={merge(styles.switchTrack, styles.switchTrackOn, betaUser)}>
                  <span style={merge(styles.switchThumb, styles.switchThumbOn, betaUser)} />
                </span>
                <input
                  type="checkbox"
                  checked={betaUser}
                  onChange={(e) => setBetaUser(e.target.checked)}
                  style={styles.switchInput}
                />
                <Sparkles size={14} strokeWidth={2} />
                <span>{betaUser ? "Beta user" : "Not a beta user"}</span>
              </label>
              {betaUser !== !!user.beta_user && (
                <div style={styles.unsavedNote}>Unsaved — click "Save profile" to apply.</div>
              )}

              {profileErr && <div style={styles.loginErr}>{profileErr}</div>}
              {profileMsg && <div style={styles.okBox}>{profileMsg}</div>}

              <button type="submit" style={styles.primaryBtn} disabled={savingProfile}>
                <Save size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
                {savingProfile ? "Saving…" : "Save profile"}
              </button>
            </form>

            {/* Password card */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Set password</h2>
              <p style={styles.cardHint}>
                Sets a new password for this user immediately. Any pending reset link is invalidated.
              </p>
              <label style={styles.label}>New password</label>
              <input
                style={styles.input}
                type={showPw ? "text" : "password"}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
              <label style={styles.label}>Confirm password</label>
              <input
                style={styles.input}
                type={showPw ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
              />

              <div style={styles.btnRow}>
                <button type="button" style={styles.secondaryBtn} onClick={generatePw}>
                  <Wand2 size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
                  Generate password
                </button>
                <button type="button" style={styles.secondaryBtn} onClick={() => setShowPw((v) => !v)}>
                  {showPw
                    ? <EyeOff size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
                    : <Eye size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />}
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>

              <p style={styles.cardHint}>{PASSWORD_HINT}</p>

              {pwErr && <div style={styles.loginErr}>{pwErr}</div>}
              {pwMsg && <div style={styles.okBox}>{pwMsg}</div>}

              <div style={styles.btnRow}>
                <button
                  type="button"
                  style={styles.primaryBtn}
                  disabled={!!savingPw}
                  onClick={() => savePassword(false)}
                >
                  <KeyRound size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
                  {savingPw === "password" ? "Updating…" : "Update password"}
                </button>
                <button
                  type="button"
                  style={{ ...styles.secondaryBtn, ...(user.user_enabled ? {} : styles.checkRowDisabled) }}
                  disabled={!!savingPw || !user.user_enabled}
                  title={
                    !user.user_enabled
                      ? "This account is still disabled — toggle it on in the Profile card and click \"Save profile\" first"
                      : (pw1 || pw2)
                        ? "Sets the password above and emails it to the user"
                        : "Emails a secure link the user can use to set their own password, without changing their current one"
                  }
                  onClick={onSendEmailClick}
                >
                  <Mail size={14} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />
                  {savingPw === "email" ? "Sending…" : (pw1 || pw2) ? "Send email with credentials" : "Send login link"}
                </button>
              </div>
              {!user.user_enabled && (
                <div style={styles.unsavedNote}>
                  This account is disabled. Enable it in the Profile card above and click "Save profile" before sending credentials.
                </div>
              )}
              {user.user_enabled && !(pw1 || pw2) && (
                <p style={styles.cardHint}>
                  Leave the password fields blank to email a link the user can use to set their own
                  password, without changing their current one.
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: { fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0", letterSpacing: "-0.3px", color: COLORS.ink },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box", background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: { background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", fontSize: 12 },
  okBox: { background: COLORS.okBg, color: COLORS.ok, border: `1px solid ${COLORS.okBorder}`, borderRadius: 6, padding: "8px 10px", fontSize: 12 },
  loginBtn: { background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8, padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS, cursor: "pointer" },
  loginGhost: { background: "transparent", color: COLORS.inkSoft, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer" },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 980, margin: "0 auto" },
  backLink: { background: "transparent", border: "none", color: COLORS.inkSoft, fontSize: 13, fontWeight: 600, fontFamily: SANS, cursor: "pointer", padding: 0, marginBottom: 16 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 24 },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0", fontFamily: MONO },

  errorBox: { padding: 16, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, marginBottom: 16 },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 },
  card: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "22px 22px 24px", display: "flex", flexDirection: "column", gap: 10 },
  cardTitle: { fontFamily: SERIF, fontSize: 20, fontWeight: 600, margin: "0 0 4px 0" },
  cardHint: { fontSize: 12, color: COLORS.inkMuted, margin: "0 0 6px 0", lineHeight: 1.5 },
  noteBox: { background: COLORS.bgAlt, color: COLORS.inkSoft, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "8px 10px", fontSize: 12, lineHeight: 1.4 },
  betaEntry: { marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` },
  betaEntryDate: { fontFamily: MONO, fontSize: 10.5, color: COLORS.inkMuted, marginBottom: 3 },
  label: { fontSize: 12, fontWeight: 600, color: COLORS.inkSoft, marginTop: 4 },
  input: {
    width: "100%", boxSizing: "border-box", background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  checkRow: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.ink, marginTop: 8, cursor: "pointer" },
  checkRowDisabled: { opacity: 0.5, cursor: "not-allowed" },
  unsavedNote: { fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginTop: 8 },
  betaBadge: { display: "inline-block", verticalAlign: "middle", marginLeft: 10, background: COLORS.dark, color: "#fff", borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" },
  switchRow: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: COLORS.ink, marginTop: 8, cursor: "pointer" },
  switchInput: { position: "absolute", opacity: 0, width: 0, height: 0 },
  switchTrack: { position: "relative", width: 34, height: 20, borderRadius: 999, background: COLORS.borderStrong, transition: "background 0.15s", flexShrink: 0 },
  switchTrackOn: { background: COLORS.ok },
  switchThumb: { position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.25)" },
  switchThumbOn: { left: 16 },
  primaryBtn: { marginTop: 12, background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8, padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS, cursor: "pointer" },
  btnRow: { display: "flex", flexWrap: "wrap", gap: 10 },
  secondaryBtn: { marginTop: 12, background: "transparent", color: COLORS.dark, border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8, padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS, cursor: "pointer" },
};
